import styles from './Gdpr.module.css'
import Checkbox from './Checkbox'
import Banner from './Banner'
import Input from './Input'
import Plus from '../icons/Plus'
import Trash from '../icons/Trash'
import DropdownSelector from './DropdownSelector'
import { WarningIcon } from '../icons/Warning'
import { MetaData } from '../types/MetaData'
import { useEffect, useState } from 'react'

interface GdprProps {
  setGdpr: (value: boolean) => void
  gdpr: boolean
  gdprData: MetaData
  setGdprData: (value: MetaData) => void
  gdprErrorList: { errorType: string; errorText: string }[]
  setGdprErrorList: (value: { errorType: string; errorText: string }[]) => void
}

function Gdpr({ 
  setGdpr, 
  gdpr,
  gdprData,
  setGdprData,
  gdprErrorList,
  setGdprErrorList
}: GdprProps) {
  const [isUnder18, setIsUnder18] = useState<boolean>(gdprData.personkategori.includes('Minderårig'))
  const personTypes = ['Modell', 'Anställd', 'Praktikant', 'Privatperson', 'Leverantör']
  
  useEffect(() => {
    if (isUnder18 && !gdprData.personkategori.includes('Minderårig')) {
      setGdprData({
        ...gdprData,
        personkategori: [
          ...gdprData.personkategori,
          'Minderårig'
        ],
      })
    }
    if (!isUnder18 && gdprData.personkategori.includes('Minderårig')) {
      setGdprData({ ...gdprData, personkategori: gdprData.personkategori.filter((type) => type !== 'Minderårig') })
    }
  }, [isUnder18])

  const addRow = () => {
    setGdprData({ ...gdprData, avtalsId: [...gdprData.avtalsId, ''] })
  }

  const handleAgreementTypeChange = (index: number, value: string) => {
    const newAgreementIds = [...gdprData.avtalsId]
    newAgreementIds[index] = value
    setGdprData({ ...gdprData, avtalsId: newAgreementIds })
    const updatedErrorList = (gdprErrorList ?? []).filter((error) => error.errorType !== 'agreementIds')
    setGdprErrorList?.(updatedErrorList)
  }

  const deleteRow = (index: number) => {
    const newAgreementIds = [...gdprData.avtalsId]
    newAgreementIds.splice(index, 1)
    setGdprData({ ...gdprData, avtalsId: newAgreementIds })
  }

  const handlePersonTypeChange = (value: string[]) => {
    setGdprData({
      ...gdprData,
      personkategori: value
    })
    if (!value.includes('Minderårig') && isUnder18) {
      setIsUnder18(false)
    }
    const updatedErrorList = (gdprErrorList ?? []).filter((error) => error.errorType !== 'personType')
    setGdprErrorList?.(updatedErrorList)
  }

  const handlePhotoDate = (value: string) => {
    setGdprData({ ...gdprData, fotoDatum: value })
    const updatedErrorList = (gdprErrorList ?? []).filter((error) => error.errorType !== 'photoDate')
    setGdprErrorList?.(updatedErrorList)
  }

  const handlePhotoDeletionDate = (value: string) => {
    setGdprData({ ...gdprData, raderingsDatum: value })
    const updatedErrorList = (gdprErrorList ?? []).filter((error) => error.errorType !== 'photoDeletionDate')
    setGdprErrorList?.(updatedErrorList)
  }

  return (
    <div className={styles.gdprContainer}>
      <Checkbox 
        id="gdpr-checkbox" 
        label="Ja, det förekommer person/personer i materialet (GDPR säkring)" 
        checked={gdpr}
        onChange={() => setGdpr(!gdpr)} 
      />
      {!gdpr && (
        <Banner theme="info">
          <p className={styles.bannerText}>
            Enligt dataskyddsförordningen (GDPR) är all information som kan kopplas till en person som är i livet personuppgifter, 
            inklusive bilder, teckningar och filmer som avbildar människor. 
            Läs mer på <a href="https://www.imy.se" className={styles.bannerLink} target="_blank" rel="noreferrer">imy.se</a></p>
        </Banner>
      )}
      {gdpr && (
        <div className={styles.gdprForm}>
          <Checkbox
            id="gdpr-checkbox-minor"
            label="Ja, minst en person är under 18 år"
            checked={isUnder18}
            onChange={(e) => setIsUnder18(e.target.checked)}
          />
          <div className={styles.gdprFormSection}>
            <h3 className={styles.modalHeading}>Typ av person*</h3>
            <DropdownSelector
              placeholder="Välj typ av person"
              onChange={(value) => handlePersonTypeChange(value)}
              dropdownItems={personTypes} 
              checkedItems={gdprData.personkategori}
              error={gdprErrorList?.some((error) => error.errorType === 'personType') ?? false}
              errorText={gdprErrorList?.find((error) => error.errorType === 'personType')?.errorText}
            />
          </div>
          <div className={styles.gdprFormSection}> 
            <h3 className={styles.modalHeading}>Referens / Avtals ID*</h3>
            {(gdprData.avtalsId.length > 0 ? gdprData.avtalsId : ['']).map((agreementId, index) => (
              <Input 
                key={index}
                id={`gdpr-person-${index}`}  
                type="text" 
                placeholder="Skriv namn på person eller Avtals ID" 
                value={agreementId || ''}
                onChange={(e) => handleAgreementTypeChange(index, e.target.value)}
                required={index === 0}
                icon={index > 0 ? <button onClick={() => deleteRow(index)} className={styles.deleteButton}><Trash className={styles.deleteIcon}/></button>: undefined}
                error={index === 0 && gdprErrorList?.some((error) => error.errorType === 'agreementIds')}
                errorText={index === 0 ? gdprErrorList?.find((error) => error.errorType === 'agreementIds')?.errorText : undefined}
                errorIcon={<WarningIcon color={'var(--redMid)'} className={styles.errorIcon}/>}
              />
            ))}
            <button className={styles.addButton} onClick={addRow}>
              <Plus color='var(--redMid)' className={styles.addIcon} /> 
              Lägg till ytterligare en rad
            </button>
          </div>
          {(gdprData.personkategori.includes('Praktikant') || gdprData.personkategori.includes('Privatperson')) && (
            <>
              <div className={styles.gdprFormSection}>
                <h3 className={styles.modalHeading}>Fotodatum*</h3>
                <Input 
                  id="gdpr-photoDate"
                  type="text"
                  placeholder="Ange fotodatum"
                  value={gdprData.fotoDatum}
                  onChange={(e) => { handlePhotoDate(e.target.value) } }
                  required={true} 
                  error={gdprErrorList?.some((error) => error.errorType === 'photoDate')}
                  errorText={gdprErrorList?.find((error) => error.errorType === 'photoDate')?.errorText}
                  errorIcon={<WarningIcon color={'var(--redMid)'} className={styles.errorIcon}/>}
                />
                <label className={styles.label} htmlFor="gdpr-photoDate">
                  Format: ÅÅÅÅ-MM-DD
                </label>
              </div>
              <div className={styles.gdprFormSection}>
                <h3 className={styles.modalHeading}>Raderingsdatum*</h3>
                <Input 
                  id="gdpr-photoDeletionDate"
                  type="text"
                  placeholder="Ange raderingsdatum"
                  value={gdprData.raderingsDatum}
                  onChange={(e) => { handlePhotoDeletionDate(e.target.value) } }
                  required={true} 
                  error={gdprErrorList?.some((error) => error.errorType === 'photoDeletionDate')}
                  errorText={gdprErrorList?.find((error) => error.errorType === 'photoDeletionDate')?.errorText}
                  errorIcon={<WarningIcon color={'var(--redMid)'} className={styles.errorIcon}/>}
                />
                <label className={styles.label} htmlFor="gdpr-photoDeletionDate">
                  Format: ÅÅÅÅ-MM-DD
                </label>
              </div>
              <Banner theme="info">
                <p className={styles.bannerText}>
                  Materialet får inte sparas längre än nödvändigt. Lagringstiden ska motsvara det syfte som avsågs vid insamlingen. 
                  Som längst kan filen lagras i 13 månader från foto- eller inspelningstillfället.
                </p>
              </Banner>
            </>
          )} 
     
        </div>

      )}
    </div>
  )
}

export default Gdpr